import type { ReactNode } from 'react';
import { cn } from './cn';

type Opt = { label: ReactNode; value: string | number; icon?: ReactNode; disabled?: boolean };

/** Ant Design `Segmented` replacement — a pill toggle group. */
export function Segmented({
  options, value, onChange, block, size = 'middle', disabled, className,
}: {
  options: (string | number | Opt)[];
  value?: string | number;
  onChange?: (v: string | number) => void;
  block?: boolean;
  size?: 'small' | 'middle' | 'large';
  disabled?: boolean;
  className?: string;
}) {
  const opts: Opt[] = options.map((o) => (typeof o === 'object' ? o : { label: String(o), value: o }));
  // On the canonical control scale (sm 32 / md 36 / lg 44) so a Segmented lines up with the Input/
  // Select/Button beside it in a toolbar row.
  const h = size === 'small' ? 'h-8 text-[13px]' : size === 'large' ? 'h-11 text-[15px]' : 'h-9 text-sm';
  return (
    <div className={cn('inline-flex items-center gap-0.5 rounded-control bg-surface-2 p-0.5', block && 'flex w-full', className)} role="tablist">
      {opts.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={disabled || o.disabled}
            onClick={() => onChange?.(o.value)}
            className={cn(
              'inline-flex items-center justify-center gap-1.5 rounded-[6px] px-3 font-medium transition-colors [&>svg]:size-4', h,
              block && 'flex-1',
              active ? 'bg-surface text-content shadow-soft' : 'text-muted hover:text-content',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
            )}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
